/**
 * useFieldLock — sudo-mode hook for editor screens.
 *
 * Usage:
 *   const { isLocked, requireUnlock, ConfirmModal } = useFieldLock("partner.successful_transactions");
 *   ...
 *   <button disabled={isLocked("bonus_value")} onClick={() => requireUnlock(() => doEdit())} />
 *   <ConfirmModal />
 *
 * Strategy:
 *   - Reads /api/auth/lock-config on mount → caches user's locked field list + sudo expiry.
 *   - `requireUnlock(callback)` runs callback immediately if no fields on this page are locked
 *     OR sudo is currently active. Otherwise shows a modal that asks for the master code,
 *     POSTs to /api/auth/master-code/verify, and runs the callback on success.
 *   - Sudo expiry is enforced server-side (30 min). Frontend just tracks `unlocked_until`
 *     to keep the UI honest and re-prompt naturally when the window expires.
 *   - When a server response returns HTTP 423 (field_locked), we surface the modal too —
 *     belt + braces against stale sudo state.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2, KeyRound, X } from "lucide-react";
import api, { formatDetail } from "@/lib/api";

let _cache = null;

async function fetchLockConfig(force = false) {
    if (_cache && !force) return _cache;
    const r = await api.get("/auth/lock-config");
    _cache = r.data;
    return _cache;
}

export function invalidateLockCache() {
    _cache = null;
}

export function useFieldLock(pageKey) {
    const [cfg, setCfg] = useState(_cache);
    const [modalState, setModalState] = useState(null); // {cb, fields}
    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const callbackRef = useRef(null);

    useEffect(() => {
        let alive = true;
        fetchLockConfig().then((c) => { if (alive) setCfg(c); }).catch(() => {});
        return () => { alive = false; };
    }, []);

    const lockedFields = useMemo(() => {
        return (cfg?.field_lock_config?.[pageKey] || []);
    }, [cfg, pageKey]);

    const sudoActive = useMemo(() => {
        if (!cfg?.unlocked_until) return false;
        return new Date(cfg.unlocked_until).getTime() > Date.now();
    }, [cfg]);

    const isLocked = useCallback((field) => {
        if (!lockedFields.includes(field)) return false;
        return !sudoActive;
    }, [lockedFields, sudoActive]);

    const hasAnyLocks = lockedFields.length > 0;

    const requireUnlock = useCallback((cb, opts = {}) => {
        // No locks on this page → run immediately
        if (!hasAnyLocks) { cb(); return; }
        // Sudo active → run immediately
        if (sudoActive) { cb(); return; }
        // Otherwise prompt
        callbackRef.current = cb;
        setModalState({ fields: opts.fields || lockedFields });
        setCode(""); setErr("");
    }, [hasAnyLocks, sudoActive, lockedFields]);

    const submitCode = async (e) => {
        e?.preventDefault?.();
        setBusy(true); setErr("");
        try {
            const r = await api.post("/auth/master-code/verify", { code });
            if (r.data?.ok) {
                _cache = null;
                const fresh = await fetchLockConfig(true);
                setCfg(fresh);
                setModalState(null);
                if (callbackRef.current) { const cb = callbackRef.current; callbackRef.current = null; cb(); }
            } else {
                setErr(r.data?.error || "Invalid code");
            }
        } catch (e2) {
            setErr(formatDetail(e2?.response?.data?.detail) || e2.message);
        } finally { setBusy(false); }
    };

    const ConfirmModal = () => {
        if (!modalState) return null;
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="field-lock-modal">
                <div className="card w-full max-w-md p-5 m-4 bg-white">
                    <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <KeyRound className="w-5 h-5 text-amber-600" />
                            <h3 className="font-semibold">Master code required</h3>
                        </div>
                        <button type="button" data-testid="field-lock-close" onClick={() => { setModalState(null); callbackRef.current = null; }} className="text-slate-400 hover:text-slate-800">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                    <p className="text-xs text-slate-500 mb-3">
                        Editing <strong>{(modalState.fields || []).join(", ")}</strong> requires your master code.
                        After unlocking, you have <strong>30 minutes</strong> of edit-mode before re-prompting.
                    </p>
                    <form onSubmit={submitCode} className="space-y-3">
                        <input data-testid="field-lock-code" autoFocus type="password" required value={code}
                               onChange={(e) => setCode(e.target.value)} className="input"
                               placeholder="Enter master code" />
                        {err && <div data-testid="field-lock-err" className="text-rose-600 text-xs">{err}</div>}
                        <div className="flex justify-end gap-2">
                            <button type="button" onClick={() => { setModalState(null); callbackRef.current = null; }} className="btn-secondary px-3 py-1.5 rounded text-xs">Cancel</button>
                            <button data-testid="field-lock-submit" disabled={busy || !code} type="submit" className="btn-primary px-4 py-1.5 rounded text-xs flex items-center gap-2">
                                {busy && <Loader2 className="w-3 h-3 animate-spin" />} Unlock
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    };

    const lock = async () => {
        try { await api.post("/auth/master-code/lock"); } catch { /* noop */ }
        _cache = null;
        setCfg(await fetchLockConfig(true));
    };

    return {
        cfg, lockedFields, sudoActive, hasAnyLocks,
        isLocked, requireUnlock, ConfirmModal, lock,
        reload: async () => { _cache = null; setCfg(await fetchLockConfig(true)); },
    };
}
